// MARK://dhk8/pos/features/env/EnvSwitcher
// Stripe-style segmented "Pruebas | Vivo" pill that lives in the topbar meta
// cluster (`.pos-topbar__meta`). Tapping the inactive segment flips the active
// env on the auth-store (which re-points every apiFetch tenantId) after a
// confirm when going to "Vivo". Tenant-level — distinct from per-session
// training mode.
//
// Visible only to `owner` / `manager` — floor staff can't switch the till's env.
// If the account (live/test tenant pair) hasn't loaded yet, the pill still flips
// `activeEnv` (so the SandboxBanner shows/hides) but shows a small hint that the
// account is still loading; the per-env tenantId then resolves once it does.
// Themed via --pos-* tokens, dark + light; sits in the 44pt-tall topbar.

import { useAuthStore, type ActiveEnv } from '../../stores/auth-store.js'
import { useConfirm } from '../../components/ConfirmDialog.js'
import { t } from '../../i18n/es-MX.js'

const SWITCH_ROLES = new Set(['owner', 'manager'])

export default function EnvSwitcher() {
  const user = useAuthStore((s) => s.user)
  const activeEnv = useAuthStore((s) => s.activeEnv)
  const setActiveEnv = useAuthStore((s) => s.setActiveEnv)
  const liveTenantId = useAuthStore((s) => s.liveTenantId)
  const testTenantId = useAuthStore((s) => s.testTenantId)
  const confirm = useConfirm()

  // Floor staff (cashier/waiter/kitchen) can't see/flip the env.
  if (!user || !SWITCH_ROLES.has(user.role)) return null

  const accountLoaded = Boolean(liveTenantId || testTenantId)

  async function pick(env: ActiveEnv) {
    if (env === activeEnv) return
    if (env === 'live') {
      const ok = await confirm({
        title: 'Cambiar a modo Vivo',
        message: t('env.confirmLive'),
        confirmLabel: 'Sí, operar en vivo',
        cancelLabel: 'Cancelar',
        tone: 'danger',
      })
      if (!ok) return
    }
    try {
      await setActiveEnv(env)
      // Recarga dura para que TODAS las vistas (incl. las que hacen apiFetch
      // directo en useEffect, no React Query) re-pidan data con el tenant/JWT
      // del nuevo entorno. Sin esto, floor/menu/reportes muestran data del
      // entorno anterior hasta navegar manualmente. El JWT y activeEnv ya
      // quedaron persistidos por setActiveEnv → la sesión sobrevive al reload.
      if (typeof window !== 'undefined') window.location.reload()
      return
    } catch (err) {
      // FORBIDDEN si no hay user clone en target tenant (account sin env test
      // provisionado). UX simple: alert + no cambio de state. El topbar pill
      // queda en el env actual.
      const msg = err instanceof Error ? err.message : t('env.switchFailed')
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert(`${t('env.switchFailed')}: ${msg}`)
      }
    }
  }

  return (
    <div
      className="pos-env-switch"
      role="group"
      aria-label={t('env.switcherLabel')}
      data-env={activeEnv}
      title={accountLoaded ? undefined : t('env.accountLoading')}
    >
      <button
        type="button"
        className={`pos-env-switch__seg${activeEnv === 'test' ? ' is-active' : ''}`}
        aria-pressed={activeEnv === 'test'}
        onClick={() => pick('test')}
      >
        {t('env.test')}
      </button>
      <button
        type="button"
        className={`pos-env-switch__seg pos-env-switch__seg--live${activeEnv === 'live' ? ' is-active' : ''}`}
        aria-pressed={activeEnv === 'live'}
        onClick={() => pick('live')}
      >
        {t('env.live')}
      </button>
      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .pos-env-switch {
    display: inline-flex;
    align-items: stretch;
    gap: 2px;
    padding: 2px;
    border-radius: var(--pos-radius-pill);
    background: var(--pos-elevated);
    border: 1px solid var(--pos-separator);
    user-select: none;
  }
  .pos-env-switch__seg {
    appearance: none;
    border: none;
    border-radius: var(--pos-radius-pill);
    min-height: 30px;
    padding: 0 var(--pos-space-3);
    background: transparent;
    color: var(--pos-fg-tertiary);
    font-family: var(--font-display, var(--font-sans));
    font-size: var(--pos-text-xs);
    font-weight: var(--pos-weight-semibold);
    letter-spacing: var(--pos-tracking-snug);
    cursor: pointer;
    transition: background var(--pos-duration-fast) var(--pos-ease-out),
                color var(--pos-duration-fast) var(--pos-ease-out);
  }
  .pos-env-switch__seg:hover { color: var(--pos-fg-secondary); }
  .pos-env-switch__seg.is-active { background: var(--pos-warning); color: #2A1B00; }
  .pos-env-switch__seg--live.is-active { background: var(--pos-tint-fill); color: var(--pos-tint-on); }
  @media (max-width: 520px) {
    .pos-env-switch__seg { padding: 0 var(--pos-space-2); font-size: var(--pos-text-xxs); }
  }
`
